import {
  calculateDm2016FootnoteAwareReferenceBundleWithHashes,
  calculateDm2016FootnoteAwareReferenceCaseWithHashes,
  calculateDm2016ReferenceCaseForVerification,
  dm2016AtomicScopeForDestinationCode,
  resolveDm2016FootnoteEffects,
  type Dm2016AtomicScope,
  type Dm2016FootnoteContext,
  type Dm2016FootnoteResolution,
  type Dm2016ReferenceCalculationResult,
  type Dm2016ReferenceInterpretationMode,
  type HashedDm2016FootnoteAwareReferenceCalculationResult,
  type HashedDm2016FootnoteAwareReferenceBundleResult,
} from "@rcprofessionale/calc-core";

import dm2016RulesetJson from "../../../docs/calcolatore/rulesets/dm2016-annex-i13-core-v1.draft.json";
import z1Json from "../../../docs/calcolatore/trascrizioni/dm2016-z1-v1.draft.json";
import z2MatrixJson from "../../../docs/calcolatore/trascrizioni/dm2016-z2-matrix-v1.draft.json";
import z2ServiceCatalogJson from "../../../docs/calcolatore/trascrizioni/dm2016-z2-service-catalog-v1.draft.json";
import z2SelectionRulesJson from "../../../docs/calcolatore/trascrizioni/dm2016-z2-selection-rules-v1.draft.json";

export const DM2016_LAB_RULESET_ID = dm2016RulesetJson.ruleset_id;
export const DM2016_LAB_RULESET_VERSION = dm2016RulesetJson.version;

const CATEGORY_LABELS: Readonly<Record<string, string>> = {
  EDILIZIA: "Edilizia",
  STRUTTURE: "Strutture",
  IMPIANTI: "Impianti",
  VIABILITA: "Viabilità",
  IDRAULICA: "Idraulica",
  TIC: "Tecnologie dell’informazione e comunicazione",
  PAESAGGIO_AMBIENTE_NATURALIZZAZIONE_AGROALIMENTARE_ZOOTECNICA_RURALITA_FORESTE:
    "Paesaggio, ambiente e ruralità",
  TERRITORIO_URBANISTICA: "Territorio e urbanistica",
};

const QCI09_CNI_MODE_WARNING =
  "QcI.09: sulla parte eccedente € 500.000 è applicato Q 0,12. La Tavola Z-2 del D.M. 17/06/2016 stampa 0,012; la Circolare CNI n. 893/2022 lo segnala come refuso e indica 0,12, coerente con il D.M. 143/2013. Non risulta una rettifica normativa formale del valore stampato.";

const QCI09_LITERAL_MODE_WARNING =
  "QcI.09: sulla parte eccedente € 500.000 è applicato Q 0,012, valore letterale della Tavola Z-2 del D.M. 17/06/2016. La Circolare CNI n. 893/2022 lo considera un refuso e indica 0,12, ma non risulta una rettifica normativa formale.";

export const DM2016_LINKED_SERVICE_OPTIONS = Object.freeze([
  Object.freeze({ code: "QcI.07", label: "Perizia di variante in corso d’opera" }),
  Object.freeze({ code: "QcI.08", label: "Nuove opere e nuovi prezzi" }),
  Object.freeze({ code: "QdI.01", label: "Collaudo tecnico-amministrativo" }),
  Object.freeze({ code: "QdI.05", label: "Attestato di certificazione energetica" }),
  Object.freeze({ code: "QbII.22", label: "Diagnosi energetica" }),
] as const);

export type Dm2016LinkedServiceCode =
  typeof DM2016_LINKED_SERVICE_OPTIONS[number]["code"];

export interface Dm2016LinkedBundleCalculationInput {
  readonly destinationCode: string;
  readonly services: readonly {
    readonly serviceCode: Dm2016LinkedServiceCode;
    readonly valueV?: string;
  }[];
  readonly footnoteContext: Dm2016FootnoteContext;
  readonly interpretationMode: Dm2016ReferenceInterpretationMode;
}

export interface HashedDm2016LinkedBundleCalculationResult
  extends HashedDm2016FootnoteAwareReferenceBundleResult {
  readonly destination: Dm2016LabDestination;
  readonly serviceLabels: Readonly<Record<Dm2016LinkedServiceCode, string>>;
  readonly source: Dm2016LabCatalog["source"];
}

export interface Dm2016LabDestination {
  readonly categoryCode: string;
  readonly code: string;
  readonly complexityG: string;
  readonly description: string;
  readonly atomicScope: Dm2016AtomicScope;
}

export interface Dm2016LabCategory {
  readonly code: string;
  readonly label: string;
  readonly destinations: readonly Dm2016LabDestination[];
}

export interface Dm2016LabService {
  readonly code: string;
  readonly label: string;
  readonly selectorKind: "work_value" | "population";
  readonly multiplierKind: "none" | "staff_count";
  readonly multiplierLabel: string | null;
  readonly planningContextRequired: boolean;
  readonly planningLevelRequirement: "general" | "executive" | null;
  readonly availability: "single_service" | "linked_workflow";
  readonly proceduralInputs: readonly Dm2016ProceduralInput[];
  readonly footnoteRefs: readonly number[];
  readonly comparisonCaseCount: number;
  readonly phaseCode: string;
  readonly phaseLabel: string;
  readonly pdfPage: number;
  readonly gazettePage: number;
  readonly variantCount: number;
}

export type Dm2016ProceduralInput =
  | "procurement_model"
  | "tender_base_project"
  | "public_works_concession";

export interface Dm2016LabCatalog {
  readonly categories: readonly Dm2016LabCategory[];
  readonly services: readonly Dm2016LabService[];
  readonly source: {
    readonly publication: string;
    readonly sha256: string;
  };
}

export interface Dm2016LabCalculationInput {
  readonly destinationCode: string;
  readonly serviceCode: Dm2016LabService["code"];
  readonly valueV: string;
  readonly inhabitants?: string;
  readonly multiplier?: string;
  readonly planning?: NonNullable<Dm2016FootnoteContext["planning"]>;
  readonly procurementModel?: NonNullable<Dm2016FootnoteContext["procurementModel"]>;
  readonly tenderBaseProject?: boolean;
  readonly publicWorksConcession?: boolean;
  readonly interpretationMode: Dm2016ReferenceInterpretationMode;
}

export interface Dm2016LabCalculationResult {
  readonly rulesetId: string;
  readonly rulesetVersion: string;
  readonly professionalUseAllowed: false;
  readonly certifiedEquoCompenso: false;
  readonly releaseStatus: "local_unverified";
  readonly destination: Dm2016LabDestination;
  readonly service: Dm2016LabService;
  readonly source: Dm2016LabCatalog["source"] & {
    readonly locator: string;
  };
  readonly warnings: readonly string[];
  readonly footnoteResolution: Dm2016FootnoteResolution;
  readonly reference: Dm2016ReferenceCalculationResult;
}

export interface HashedDm2016LabCalculationResult
  extends Dm2016LabCalculationResult {
  readonly engineVersion: string;
  readonly inputSha256: string;
  readonly resultSha256: string;
  readonly normalizedInput:
    HashedDm2016FootnoteAwareReferenceCalculationResult["normalizedInput"];
}

type UnknownRecord = Record<string, unknown>;

function record(value: unknown, label: string): UnknownRecord {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`${label} non valido`);
  }
  return value as UnknownRecord;
}

function array(value: unknown, label: string): unknown[] {
  if (!Array.isArray(value)) throw new Error(`${label} non valido`);
  return value;
}

function string(value: unknown, label: string): string {
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(`${label} non valido`);
  }
  return value;
}

function number(value: unknown, label: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new Error(`${label} non valido`);
  }
  return value;
}

function buildCategories(): readonly Dm2016LabCategory[] {
  const root = record(z1Json, "Tavola Z-1");
  return array(root.categories, "Categorie Z-1").map((categoryValue) => {
    const category = record(categoryValue, "Categoria Z-1");
    const categoryCode = string(category.code, "Codice categoria Z-1");
    const destinations = array(category.destinations, "Destinazioni Z-1").map(
      (destinationValue): Dm2016LabDestination => {
        const destination = record(destinationValue, "Destinazione Z-1");
        const code = string(destination.code, "Codice destinazione Z-1");
        return Object.freeze({
          categoryCode,
          code,
          complexityG: string(destination.g, "Coefficiente G"),
          description: string(destination.description, "Descrizione destinazione"),
          atomicScope: dm2016AtomicScopeForDestinationCode(code),
        });
      },
    );
    return Object.freeze({
      code: categoryCode,
      label: CATEGORY_LABELS[categoryCode] ?? categoryCode,
      destinations: Object.freeze(destinations),
    });
  });
}

function buildServices(): readonly Dm2016LabService[] {
  const matrix = record(z2MatrixJson, "Matrice Z-2");
  const variantsByService = new Map(
    array(matrix.services, "Servizi matrice Z-2").map((serviceValue) => {
      const service = record(serviceValue, "Servizio matrice Z-2");
      return [
        string(service.code, "Codice matrice Z-2"),
        array(service.variants, "Varianti matrice Z-2").length,
      ] as const;
    }),
  );
  const root = record(z2ServiceCatalogJson, "Catalogo servizi Z-2");
  const services = array(root.services, "Servizi catalogo Z-2").map((serviceValue) => {
    const service = record(serviceValue, "Servizio catalogo Z-2");
    const code = string(service.code, "Codice servizio Z-2");
    const application = record(service.application, `Applicazione ${code}`);
    const variantCount = variantsByService.get(code);
    if (variantCount === undefined) {
      throw new Error(`Metadati incompleti per ${code}`);
    }
    const tariffRows = number(service.tariff_rows, `Righe tariffarie ${code}`);
    if (tariffRows !== variantCount) {
      throw new Error(`Numero varianti incoerente per ${code}: ${tariffRows} != ${variantCount}`);
    }
    const availabilityValue = string(application.availability, `Disponibilità ${code}`);
    if (!["single_service", "linked_workflow"].includes(availabilityValue)) {
      throw new Error(`Disponibilità non valida per ${code}`);
    }
    const availability = availabilityValue as Dm2016LabService["availability"];
    const selectorValue = string(application.selector_kind, `Selettore ${code}`);
    if (!["population", "work_value"].includes(selectorValue)) {
      throw new Error(`Selettore non valido per ${code}`);
    }
    const multiplierValue = string(application.multiplier_kind, `Moltiplicatore ${code}`);
    if (!["none", "staff_count"].includes(multiplierValue)) {
      throw new Error(`Moltiplicatore non valido per ${code}`);
    }
    const planningRequirementValue = application.planning_level_requirement;
    if (
      planningRequirementValue !== null
      && planningRequirementValue !== "general"
      && planningRequirementValue !== "executive"
    ) throw new Error(`Livello pianificazione non valido per ${code}`);
    if (typeof application.planning_context_required !== "boolean") {
      throw new Error(`Obbligo contesto pianificazione non valido per ${code}`);
    }
    const footnoteRefs = array(service.printed_footnote_refs, `Note ${code}`).map(
      (value) => number(value, `Nota ${code}`),
    );
    const proceduralInputs = array(
      application.procedural_inputs,
      `Input procedurali ${code}`,
    ).map((value) => {
      const parsed = string(value, `Input procedurale ${code}`);
      if (![
        "procurement_model", "tender_base_project", "public_works_concession",
      ].includes(parsed)) throw new Error(`Input procedurale non valido per ${code}`);
      return parsed as Dm2016ProceduralInput;
    });
    return Object.freeze({
      code,
      label: string(service.description_official_normalized, `Descrizione ${code}`),
      selectorKind: selectorValue as Dm2016LabService["selectorKind"],
      multiplierKind: multiplierValue as Dm2016LabService["multiplierKind"],
      multiplierLabel: application.multiplier_label === null
        ? null
        : string(application.multiplier_label, `Etichetta moltiplicatore ${code}`),
      planningContextRequired: application.planning_context_required === true,
      planningLevelRequirement: planningRequirementValue as Dm2016LabService["planningLevelRequirement"],
      availability,
      proceduralInputs: Object.freeze(proceduralInputs),
      footnoteRefs: Object.freeze(footnoteRefs),
      comparisonCaseCount: number(application.comparison_case_count, `Confronti ${code}`),
      phaseCode: string(service.phase_code, `Codice fase ${code}`),
      phaseLabel: string(service.phase_label, `Etichetta fase ${code}`),
      pdfPage: number(service.pdf_page, `Pagina PDF ${code}`),
      gazettePage: number(service.gazette_page, `Pagina G.U. ${code}`),
      variantCount,
    });
  });
  if (services.length !== 96 || variantsByService.size !== 96) {
    throw new Error(`Catalogo Z-2 incompleto: ${services.length}/96 descrizioni, ${variantsByService.size}/96 matrici`);
  }
  const serviceCodes = new Set(services.map((service) => service.code));
  if (
    serviceCodes.size !== 96
    || [...variantsByService.keys()].some((code) => !serviceCodes.has(code))
  ) throw new Error("Catalogo e matrice Z-2 hanno codici diversi o duplicati");
  if (z2ServiceCatalogJson.source.sha256 !== z2MatrixJson.source.sha256) {
    throw new Error("Catalogo e matrice Z-2 non provengono dallo stesso PDF ufficiale");
  }
  return services;
}

const catalog: Dm2016LabCatalog = Object.freeze({
  categories: Object.freeze(buildCategories()),
  services: Object.freeze(buildServices()),
  source: Object.freeze({
    publication: z2MatrixJson.source.publication,
    sha256: z2MatrixJson.source.sha256,
  }),
});

function matrixService(code: string): UnknownRecord {
  const matrix = record(z2MatrixJson, "Matrice Z-2");
  const service = array(matrix.services, "Servizi matrice Z-2").find(
    (candidate) => record(candidate, "Servizio matrice Z-2").code === code,
  );
  if (service === undefined) throw new Error(`Servizio non trovato: ${code}`);
  return record(service, "Servizio matrice Z-2");
}

export function getDm2016LabCatalog(): Dm2016LabCatalog {
  return catalog;
}

export function getDm2016LabDestination(code: string): Dm2016LabDestination | undefined {
  return catalog.categories.flatMap((category) => category.destinations)
    .find((destination) => destination.code === code);
}

export function getDm2016LabServices(destinationCode: string): readonly Dm2016LabService[] {
  const destination = getDm2016LabDestination(destinationCode);
  if (destination === undefined) return [];
  const scopes = z2MatrixJson.atomic_scope_order as readonly string[];
  const scopeIndex = scopes.indexOf(destination.atomicScope);
  if (scopeIndex < 0) return [];
  return catalog.services.filter((service) => {
    if (service.availability !== "single_service") return false;
    const variants = array(matrixService(service.code).variants, "Varianti Z-2");
    return variants.every((variantValue) => {
      const q = array(record(variantValue, "Variante Z-2").q, "Vettore Q");
      return q[scopeIndex] !== null && q[scopeIndex] !== undefined;
    });
  });
}

export function getDm2016LinkedServices(
  destinationCode: string,
): readonly typeof DM2016_LINKED_SERVICE_OPTIONS[number][] {
  const destination = getDm2016LabDestination(destinationCode);
  if (destination === undefined) return [];
  const scopes = z2MatrixJson.atomic_scope_order as readonly string[];
  const scopeIndex = scopes.indexOf(destination.atomicScope);
  if (scopeIndex < 0) return [];
  return DM2016_LINKED_SERVICE_OPTIONS.filter((service) => {
    const variants = array(matrixService(service.code).variants, "Varianti Z-2");
    return variants.every((variantValue) => {
      const q = array(record(variantValue, "Variante Z-2").q, "Vettore Q");
      return q[scopeIndex] !== null && q[scopeIndex] !== undefined;
    });
  });
}

export async function calculateDm2016LinkedBundleWithHashes(
  input: Dm2016LinkedBundleCalculationInput,
): Promise<HashedDm2016LinkedBundleCalculationResult> {
  const destination = getDm2016LabDestination(input.destinationCode);
  if (destination === undefined) {
    throw new Error(`Destinazione non disponibile: ${input.destinationCode}`);
  }
  const applicable = new Set(
    getDm2016LinkedServices(input.destinationCode).map((service) => service.code),
  );
  const unavailable = input.services.find((service) => !applicable.has(service.serviceCode));
  if (unavailable !== undefined) {
    throw new Error(
      `La prestazione ${unavailable.serviceCode} non è applicabile a ${input.destinationCode}`,
    );
  }
  const bundle = await calculateDm2016FootnoteAwareReferenceBundleWithHashes({
    z1: z1Json,
    z2Matrix: z2MatrixJson,
    selectionRules: z2SelectionRulesJson,
    destinationCode: input.destinationCode,
    services: input.services,
    footnoteContext: input.footnoteContext,
    interpretationMode: input.interpretationMode,
    identity: {
      rulesetId: DM2016_LAB_RULESET_ID,
      rulesetVersion: DM2016_LAB_RULESET_VERSION,
      sourceSha256: catalog.source.sha256,
    },
  });
  const serviceLabels = Object.fromEntries(
    DM2016_LINKED_SERVICE_OPTIONS.map((service) => [service.code, service.label]),
  ) as Record<Dm2016LinkedServiceCode, string>;
  return Object.freeze({
    ...bundle,
    destination,
    serviceLabels: Object.freeze(serviceLabels),
    source: catalog.source,
  });
}

function resolveLabSelection(input: Dm2016LabCalculationInput): {
  destination: Dm2016LabDestination;
  service: Dm2016LabService;
  footnoteResolution: Dm2016FootnoteResolution;
} {
  const destination = getDm2016LabDestination(input.destinationCode);
  if (destination === undefined) {
    throw new Error(`Destinazione non disponibile: ${input.destinationCode}`);
  }
  const service = getDm2016LabServices(input.destinationCode)
    .find((candidate) => candidate.code === input.serviceCode);
  if (service === undefined) {
    throw new Error(`La prestazione ${input.serviceCode} non è applicabile a ${input.destinationCode}`);
  }
  if (service.selectorKind === "population" && input.inhabitants === undefined) {
    throw new Error(`Indica la popolazione interessata per ${service.code}`);
  }
  if (service.selectorKind === "work_value" && input.inhabitants !== undefined) {
    throw new Error(`La popolazione non è applicabile a ${service.code}`);
  }
  if (
    service.planningLevelRequirement !== null
    && input.planning !== undefined
    && input.planning?.level !== service.planningLevelRequirement
  ) {
    throw new Error(
      `${service.code} richiede un livello di pianificazione ${service.planningLevelRequirement === "general" ? "generale" : "esecutivo o attuativo"}`,
    );
  }
  if (service.multiplierKind === "staff_count") {
    if (input.multiplier === undefined || !/^[1-9]\d*$/.test(input.multiplier)) {
      throw new Error(`${service.multiplierLabel ?? "Il numero di addetti"} deve essere un intero positivo`);
    }
  } else if (input.multiplier !== undefined) {
    throw new Error(`Il moltiplicatore non è applicabile a ${service.code}`);
  }
  const suppliedProceduralInputs: readonly [Dm2016ProceduralInput, boolean][] = [
    ["procurement_model", input.procurementModel !== undefined],
    ["tender_base_project", input.tenderBaseProject !== undefined],
    ["public_works_concession", input.publicWorksConcession !== undefined],
  ];
  const unexpectedProceduralInput = suppliedProceduralInputs.find(
    ([key, supplied]) => supplied && !service.proceduralInputs.includes(key),
  );
  if (unexpectedProceduralInput !== undefined) {
    throw new Error(
      `Il contesto ${unexpectedProceduralInput[0]} non è applicabile a ${service.code}`,
    );
  }
  const footnoteResolution = resolveDm2016FootnoteEffects({
    services: [{ serviceCode: input.serviceCode, valueV: input.valueV }],
    context: footnoteContextForLab(input),
  });
  return { destination, service, footnoteResolution };
}

function footnoteContextForLab(input: Dm2016LabCalculationInput): Dm2016FootnoteContext {
  return {
    ...(input.planning === undefined ? {} : { planning: input.planning }),
    ...(input.procurementModel === undefined
      ? {}
      : { procurementModel: input.procurementModel }),
    ...(input.tenderBaseProject === undefined
      ? {}
      : { tenderBaseProject: input.tenderBaseProject }),
    ...(input.publicWorksConcession === undefined
      ? {}
      : { publicWorksConcession: input.publicWorksConcession }),
  };
}

function assembleLabResult(
  input: Dm2016LabCalculationInput,
  selection: ReturnType<typeof resolveLabSelection>,
  reference: Dm2016ReferenceCalculationResult,
): Dm2016LabCalculationResult {
  const { destination, service } = selection;
  const qci09DisputedBandApplied =
    input.serviceCode === "QcI.09"
    && (
      destination.atomicScope === "EDILIZIA"
      || destination.atomicScope === "STRUTTURE_S01_S03"
      || destination.atomicScope === "STRUTTURE_S02_S04_S05_S06"
    )
    && reference.calculation.bands.some((band) => band.bandIndex === 1);
  const warnings = [
    ...reference.resolution.warnings.map((warning) => {
      if (warning.startsWith("Market compatibility interpolates Q")) {
        return "La modalità di confronto interpola Q anche se per questa prestazione non è stampata una nota di interpolazione.";
      }
      if (warning.startsWith("Selection rule")) {
        return `La regola di selezione ${input.serviceCode} è ancora bloccata dal gate normativo.`;
      }
      return warning;
    }),
    ...(qci09DisputedBandApplied
      ? [input.interpretationMode === "printed_source_strict"
        ? QCI09_LITERAL_MODE_WARNING
        : QCI09_CNI_MODE_WARNING]
      : []),
  ];
  return Object.freeze({
    rulesetId: DM2016_LAB_RULESET_ID,
    rulesetVersion: DM2016_LAB_RULESET_VERSION,
    professionalUseAllowed: false,
    certifiedEquoCompenso: false,
    releaseStatus: "local_unverified",
    destination,
    service,
    source: Object.freeze({
      ...catalog.source,
      locator: `PDF ${service.pdfPage} / G.U. ${service.gazettePage}`,
    }),
    warnings: Object.freeze(warnings),
    footnoteResolution: selection.footnoteResolution,
    reference,
  });
}

function resolvedReferenceInput(
  input: Dm2016LabCalculationInput,
  selection: ReturnType<typeof resolveLabSelection>,
): Pick<Dm2016LabCalculationInput, "valueV" | "multiplier"> {
  const resolved = selection.footnoteResolution.services[0];
  if (resolved === undefined) throw new Error("Risoluzione delle note incompleta");
  return {
    valueV: resolved.valueV,
    ...(resolved.multiplier === "1"
      ? input.multiplier === undefined ? {} : { multiplier: input.multiplier }
      : { multiplier: resolved.multiplier }),
  };
}

export function calculateDm2016Lab(
  input: Dm2016LabCalculationInput,
): Dm2016LabCalculationResult {
  const selection = resolveLabSelection(input);
  const resolved = resolvedReferenceInput(input, selection);
  const reference = calculateDm2016ReferenceCaseForVerification({
    z1: z1Json,
    z2Matrix: z2MatrixJson,
    selectionRules: z2SelectionRulesJson,
    destinationCode: input.destinationCode,
    serviceCode: input.serviceCode,
    valueV: resolved.valueV,
    ...(input.inhabitants === undefined ? {} : { inhabitants: input.inhabitants }),
    ...(resolved.multiplier === undefined ? {} : { multiplier: resolved.multiplier }),
    interpretationMode: input.interpretationMode,
  });

  return assembleLabResult(input, selection, reference);
}

export async function calculateDm2016LabWithHashes(
  input: Dm2016LabCalculationInput,
): Promise<HashedDm2016LabCalculationResult> {
  const selection = resolveLabSelection(input);
  const hashed = await calculateDm2016FootnoteAwareReferenceCaseWithHashes({
    z1: z1Json,
    z2Matrix: z2MatrixJson,
    selectionRules: z2SelectionRulesJson,
    destinationCode: input.destinationCode,
    serviceCode: input.serviceCode,
    valueV: input.valueV,
    ...(input.inhabitants === undefined ? {} : { inhabitants: input.inhabitants }),
    ...(input.multiplier === undefined ? {} : { multiplier: input.multiplier }),
    interpretationMode: input.interpretationMode,
    footnoteContext: footnoteContextForLab(input),
    identity: {
      rulesetId: DM2016_LAB_RULESET_ID,
      rulesetVersion: DM2016_LAB_RULESET_VERSION,
      sourceSha256: catalog.source.sha256,
    },
  });
  const result = assembleLabResult(input, selection, hashed.reference);

  return Object.freeze({
    ...result,
    engineVersion: hashed.engineVersion,
    normalizedInput: hashed.normalizedInput,
    inputSha256: hashed.inputSha256,
    resultSha256: hashed.resultSha256,
  });
}
